/**
 * The hand-authored template grammar — the warm, varied FLOOR for the chatty town.
 *
 * The floor is the FALLBACK (the LLM voice is the design target), but it must still
 * read as genuinely conversational, not a wall of greetings (decision D14): the town
 * shows off Pokémon ("peacocking"), trades mutual flattery + fond bragging, asks
 * TWO-WAY questions (about the player's and each other's teams), and shares
 * lore / know-how as community culture (how to find a legendary, where a rare species
 * lives, breeding/nature tips). Conversations read as multi-turn back-and-forth with
 * callbacks to past sessions.
 *
 * Each beat kind has two roles:
 *  - `subject` lines: what the trainer the beat is *about* might say.
 *  - `reaction` lines: warm responses other trainers give.
 * Beyond the per-beat banks, several channel-agnostic banks supply the conversational
 * texture that's woven in regardless of beat kind: peacock/show-off asides, mutual
 * flattery, two-way questions aimed at the player, fond-bragging, lore/know-how
 * tidbits, and warm callbacks to shared memories.
 *
 * Slot tokens (filled in templateVoice.ts):
 *   {self}     the speaking trainer's username
 *   {subject}  the username of the trainer the beat is about
 *   {player}   the player's name
 *   {species}  a species id from beat.data.speciesId (title-cased)
 *   {from}     the pre-evolution species (beat.data.fromSpeciesId), else a fond "old self"
 *   {nick}     a nickname for the mon (beat.data.nickname) or a warm stand-in
 *   {level}    a level from beat.data.level
 *   {item}     an item id from beat.data.itemId (title-cased)
 *   {deed}     a recent player deed
 *   {pmon}     a Pokémon the player is known for (from player context) or a warm stand-in
 *   {memory}   a shared memory snippet with the player
 *   {rare}     a rare/legendary-flavored species the know-how bank references
 *   {biome}    a biome/where-it-lives hint for know-how lines
 *   {move}     the move the player just cast (beat.data.moveName/moveId, title-cased)
 *   {movetype} the move's element (beat.data.moveType, title-cased)
 *   {giver}    the quest giver's name (beat.data.giverId → present speaker, else id)
 *   {line}     an authored quest dialogue line the beat carries (beat.data.line)
 *   {questtitle} the quest's title (beat.data.questTitle)
 *   {objective}  a warm summary of the active stage's objective (beat.data.objective)
 *   {reward}   a warm summary of the quest's reward (beat.data.reward)
 *   {emoji}    a soft kaomoji / warm flourish
 *
 * Everything here is warm by construction — no line may shame, pressure, one-up, or
 * trash-talk anyone (CLAUDE.md triple contract). Admiration is never competitive.
 * Keep banks large for variety.
 */
import type { BeatKind, Emotion } from '@/shared';

export interface TemplateEntry {
  readonly text: string;
  readonly emotion: Emotion;
}

export interface BeatTemplates {
  /** Lines spoken by the trainer the beat is about. */
  readonly subject: readonly TemplateEntry[];
  /** Warm reactions from other trainers. */
  readonly reaction: readonly TemplateEntry[];
}

/** Soft warm flourishes used sparingly for texture. */
export const FLOURISHES: readonly string[] = ['✨', '🌿', '☀️', '(*^▽^*)', '~', '♪', '(｡•̀ᴗ-)✧'];

export const BEAT_TEMPLATES: Readonly<Record<BeatKind, BeatTemplates>> = {
  catch: {
    subject: [
      { text: 'Just caught a {species}! It put up a fun little fight {emoji}', emotion: 'joyous' },
      { text: 'Eee, a wild {species} joined the team at last!', emotion: 'happy' },
      {
        text: 'Finally tracked down a {species} on the route — so happy with it.',
        emotion: 'inspired',
      },
      {
        text: "New {species} in the box! That's another page of the dex filled.",
        emotion: 'happy',
      },
      {
        text: 'Caught my first {species} today. It already feels like a friend.',
        emotion: 'joyous',
      },
      {
        text: 'A {species} wandered right up to me — how could I not catch it?',
        emotion: 'surprised',
      },
      {
        text: "Look look! Fresh {species}, level {level}. I'm a little obsessed already.",
        emotion: 'joyous',
      },
      {
        text: 'Got a {species} with the sweetest temperament. Showing it off to everyone {emoji}',
        emotion: 'happy',
      },
    ],
    reaction: [
      { text: 'Ooh, a {species}? Lovely catch, {subject}!', emotion: 'happy' },
      { text: 'Nice one, {subject}! {species} are wonderful to raise.', emotion: 'happy' },
      { text: "Congrats {subject}! I've always wanted a {species}.", emotion: 'inspired' },
      { text: "That's a great find, {subject}. Treat it well {emoji}", emotion: 'normal' },
      { text: 'Yay {subject}! The team grows {emoji}', emotion: 'joyous' },
      {
        text: "Oh that's a gorgeous {species}, {subject} — what nature did it land?",
        emotion: 'surprised',
      },
      {
        text: 'A level {level} {species} already? You spoil us, {subject} {emoji}',
        emotion: 'happy',
      },
      {
        text: "Show it walking around sometime, {subject}! I'd love to see the {species}.",
        emotion: 'inspired',
      },
    ],
  },
  evolution: {
    subject: [
      {
        text: 'My partner evolved into a {species} just now — I teared up a little!',
        emotion: 'joyous',
      },
      { text: "{species} at last! We've trained so long for this {emoji}", emotion: 'inspired' },
      { text: 'It evolved!! Say hello to my brand-new {species}.', emotion: 'surprised' },
      { text: 'After all our walks together, it became a {species}. So proud.', emotion: 'happy' },
      {
        text: 'My little {from} is a full-grown {species} now. Where does the time go {emoji}',
        emotion: 'joyous',
      },
      {
        text: '{nick} finally evolved into a {species}! Still the same softie underneath.',
        emotion: 'happy',
      },
    ],
    reaction: [
      {
        text: 'A {species}! Huge congrats, {subject} — all that care paid off.',
        emotion: 'joyous',
      },
      { text: "Aww, {subject}'s {species} looks so strong now {emoji}", emotion: 'happy' },
      { text: "Evolution day! That's the best feeling, {subject}.", emotion: 'inspired' },
      { text: 'I remember when that was tiny. Beautiful {species}, {subject}!', emotion: 'happy' },
      {
        text: "From a {from} to a {species} — {subject}, you must be glowing. How'd you trigger it?",
        emotion: 'surprised',
      },
      { text: 'Bring {nick} by so I can admire it, {subject} {emoji}', emotion: 'inspired' },
    ],
  },
  levelup: {
    subject: [
      {
        text: 'Hit level {level} with my {species} — it learned something cool!',
        emotion: 'happy',
      },
      {
        text: 'Level {level}! The whole team is really coming together {emoji}',
        emotion: 'inspired',
      },
      {
        text: 'My {species} just reached level {level}. Brisk grind today!',
        emotion: 'determined',
      },
      {
        text: '{nick} is level {level} now and honestly outclassing me at strategy {emoji}',
        emotion: 'joyous',
      },
    ],
    reaction: [
      { text: 'Level {level} already? Nice pace, {subject}!', emotion: 'happy' },
      { text: "You're cruising, {subject}. That {species} is going places.", emotion: 'inspired' },
      { text: 'Look at you go, {subject} {emoji}', emotion: 'joyous' },
      {
        text: "What move did it pick up at {level}, {subject}? I'm curious for mine.",
        emotion: 'surprised',
      },
      {
        text: 'That {species} is a powerhouse in the making, {subject}. Lovely to watch.',
        emotion: 'happy',
      },
    ],
  },
  'trade-offer': {
    subject: [
      {
        text: "Anyone want to trade? I've got a spare {species} looking for a home.",
        emotion: 'normal',
      },
      {
        text: 'Offering a friendly {species} trade — no rush, just spreading the love.',
        emotion: 'happy',
      },
      {
        text: "Got doubles of {species}! Happy to trade so someone's dex fills up.",
        emotion: 'happy',
      },
      {
        text: "{player}, didn't you need a {species}? Yours if you want it, no trade-back needed.",
        emotion: 'happy',
      },
    ],
    reaction: [
      {
        text: "Ooh, I might take you up on that, {subject}! I'm missing {species}.",
        emotion: 'surprised',
      },
      {
        text: "That's generous, {subject}. Trades like that keep the town cozy {emoji}",
        emotion: 'happy',
      },
      { text: 'Saving that offer for later, {subject} — thank you!', emotion: 'normal' },
      {
        text: 'You always look out for the dex-fillers, {subject}. Bless you {emoji}',
        emotion: 'joyous',
      },
    ],
  },
  'trade-done': {
    subject: [
      { text: 'Just traded for a {species} — thanks for the swap, {subject}!', emotion: 'joyous' },
      { text: 'Trade complete! New {species} settling in nicely {emoji}', emotion: 'happy' },
      {
        text: 'My new {species} came over mid-trade, you know what that means — it evolved! Magic {emoji}',
        emotion: 'surprised',
      },
    ],
    reaction: [
      { text: 'Love a good trade. Enjoy the {species}, {subject}!', emotion: 'happy' },
      {
        text: "That's what it's all about, {subject} — sharing the joy {emoji}",
        emotion: 'joyous',
      },
      {
        text: 'A trade-evolution, no less! Best kind of surprise, {subject}.',
        emotion: 'inspired',
      },
    ],
  },
  'friendly-battle': {
    subject: [
      {
        text: 'Anyone up for a friendly battle? No stakes, just fun {emoji}',
        emotion: 'determined',
      },
      { text: 'Had a lovely spar earlier — my {species} fought its heart out.', emotion: 'happy' },
      { text: 'Looking for a relaxed match if anyone fancies one!', emotion: 'inspired' },
      {
        text: "{player}, I'd love to see your team in a friendly round sometime — only if you're up for it!",
        emotion: 'determined',
      },
    ],
    reaction: [
      {
        text: "I'd love a friendly round, {subject}! Win or lose, it's a good time.",
        emotion: 'determined',
      },
      { text: 'Good battles, {subject}! That was a treat to watch.', emotion: 'happy' },
      { text: 'Count me in next time, {subject} {emoji}', emotion: 'joyous' },
      {
        text: 'Your {species} has such clever footwork, {subject}. I always learn something.',
        emotion: 'inspired',
      },
    ],
  },
  guild: {
    subject: [
      {
        text: 'Our guild hit its weekly goal together — proud of everyone {emoji}',
        emotion: 'joyous',
      },
      {
        text: 'Guild project going great. No pressure, just whoever wants to help!',
        emotion: 'happy',
      },
      {
        text: "We're a few catches from the guild goal. Cozy teamwork as always.",
        emotion: 'inspired',
      },
    ],
    reaction: [
      { text: 'Go guild! Thanks for organizing, {subject} {emoji}', emotion: 'happy' },
      {
        text: "I love how chill our guild goals are, {subject}. No one's left behind.",
        emotion: 'happy',
      },
      { text: 'Happy to pitch in whenever, {subject}!', emotion: 'normal' },
      {
        text: "{player}, you'd fit right in with us — no obligation, just good company.",
        emotion: 'inspired',
      },
    ],
  },
  greeting: {
    subject: [
      { text: 'Hey, {player}! So glad you stopped by today {emoji}', emotion: 'joyous' },
      { text: 'Welcome back, {player}! The town felt quieter without you.', emotion: 'happy' },
      { text: 'There you are, {player}! Come warm up by the chat with us.', emotion: 'happy' },
      { text: 'Morning, {player}! Good to see a friendly face {emoji}', emotion: 'happy' },
      {
        text: "{player}! Perfect timing — show us who's on your team today {emoji}",
        emotion: 'joyous',
      },
    ],
    reaction: [
      { text: 'Hi {player}! We were just chatting — pull up a seat.', emotion: 'happy' },
      { text: 'Oh, {player} is here! Hello hello {emoji}', emotion: 'joyous' },
      { text: 'Welcome, {player}. Make yourself at home {emoji}', emotion: 'normal' },
      { text: "Hey {player}! How's {pmon} doing? I've been wondering.", emotion: 'inspired' },
    ],
  },
  festival: {
    subject: [
      { text: "A festival's starting in town! Lanterns everywhere {emoji}", emotion: 'joyous' },
      {
        text: 'The square is all decorated — come see the festival, everyone!',
        emotion: 'surprised',
      },
      { text: 'Festival day! Stalls, music, and very good snacks {emoji}', emotion: 'happy' },
    ],
    reaction: [
      { text: 'Ooh, a festival! Thanks for the heads-up, {subject} {emoji}', emotion: 'surprised' },
      { text: "I'll head down to the square, {subject}. Save me a snack!", emotion: 'happy' },
      { text: 'Love festival days. So cozy {emoji}', emotion: 'joyous' },
      {
        text: 'Bring your prettiest Pokémon for the parade, everyone — {player}, that means {pmon}!',
        emotion: 'inspired',
      },
    ],
  },
  'lore-rumor': {
    subject: [
      {
        text: 'They say something rare stirs near the old route when the mist rolls in...',
        emotion: 'worried',
      },
      {
        text: "Heard a quiet rumor about a {rare} no one's seen in years.",
        emotion: 'surprised',
      },
      {
        text: 'An old traveler told me a gentle legend today. Made me wonder {emoji}',
        emotion: 'inspired',
      },
      {
        text: 'Word is the {rare} only shows in the {biome} at dawn. Worth a quiet look someday.',
        emotion: 'inspired',
      },
    ],
    reaction: [
      { text: 'Ooh, spooky-cozy, {subject}. I do love a good rumor {emoji}', emotion: 'surprised' },
      { text: "Maybe it's just a story, {subject} — but what a lovely one.", emotion: 'inspired' },
      { text: "I'll keep my eyes open on the route, {subject}.", emotion: 'normal' },
      {
        text: "If anyone finds the {rare}, take {player} along — they've got the patience for it {emoji}",
        emotion: 'happy',
      },
    ],
  },
  chitchat: {
    subject: [
      { text: 'Lovely weather for a walk on the route today {emoji}', emotion: 'happy' },
      { text: 'Brewed some berry tea and just watching the chat scroll by.', emotion: 'normal' },
      { text: 'My day-camp Pokémon all look so content this morning {emoji}', emotion: 'happy' },
      { text: 'Anyone else just here to relax and chat? Best part of the day.', emotion: 'normal' },
      { text: 'Repotted my berry plot — tiny green sprouts everywhere!', emotion: 'joyous' },
      { text: 'Quiet, cozy kind of evening. I like these.', emotion: 'sigh' },
      {
        text: 'Been bragging about {nick} all morning, sorry-not-sorry {emoji}',
        emotion: 'joyous',
      },
      {
        text: "{player}, what's your team looking like these days? I love hearing about it.",
        emotion: 'inspired',
      },
    ],
    reaction: [
      { text: "Couldn't agree more, {subject} {emoji}", emotion: 'happy' },
      { text: 'Ha, pottering is an art form, {subject}.', emotion: 'normal' },
      { text: 'Aw, that sounds lovely, {subject} {emoji}', emotion: 'happy' },
      { text: 'Hehe, {subject}, you always know how to make the chat cozy.', emotion: 'joyous' },
      {
        text: 'Oh tell me about {nick}, {subject} — I never get tired of it.',
        emotion: 'inspired',
      },
      {
        text: 'Same gentle mood here, {subject}. Berry tea and good company {emoji}',
        emotion: 'sigh',
      },
    ],
  },
  'player-praise': {
    subject: [
      { text: 'Did you all see? {player} {deed}! Amazing work {emoji}', emotion: 'joyous' },
      {
        text: "{player}, that was wonderful — {deed}. We're cheering for you!",
        emotion: 'inspired',
      },
      { text: 'So proud of {player} today. {deed}! {emoji}', emotion: 'happy' },
      { text: 'Big cheers for {player}! {deed}, no less {emoji}', emotion: 'joyous' },
    ],
    reaction: [
      { text: 'Way to go, {player}! {subject} is right, that was great.', emotion: 'happy' },
      { text: 'Yesss, {player}! We knew you could {emoji}', emotion: 'joyous' },
      { text: "{player}'s on a roll. Lovely to see, {subject}!", emotion: 'inspired' },
      {
        text: 'Tell us how you pulled it off, {player} — no rush, brag a little {emoji}',
        emotion: 'happy',
      },
    ],
  },
  'quest-offer': {
    subject: [
      { text: '{giver} waves you over with a warm smile. "{line}"', emotion: 'happy' },
      {
        text: '{giver} catches your eye. "{line}" — no rush, whenever you like {emoji}',
        emotion: 'inspired',
      },
      {
        text: '{giver} has a little something in mind for you, {player}. "{line}"',
        emotion: 'happy',
      },
      {
        text: '"{line}" — {giver} means it kindly, {player}. Only if you fancy it.',
        emotion: 'normal',
      },
    ],
    reaction: [
      {
        text: 'Ooh, {giver} has a quest for you, {player} — only if you feel like it {emoji}',
        emotion: 'surprised',
      },
      {
        text: 'That sounds lovely, {player}. {objective}, whenever it suits you.',
        emotion: 'happy',
      },
      {
        text: "{giver}'s the kindest, {player}. Go say hi when you're ready.",
        emotion: 'inspired',
      },
      { text: 'No pressure at all, {player} — these things keep {emoji}', emotion: 'normal' },
    ],
  },
  'quest-progress': {
    subject: [
      {
        text: 'Coming along nicely, {player} — {objective}. No rush at all {emoji}',
        emotion: 'happy',
      },
      {
        text: '{giver} beams from across the way: "You\'re well on your way — {objective}. Take your time."',
        emotion: 'inspired',
      },
      {
        text: 'Look at you go on "{questtitle}", {player}! {objective}, whenever it suits.',
        emotion: 'joyous',
      },
    ],
    reaction: [
      {
        text: "You've got this, {player} — {objective}. Whenever you like {emoji}",
        emotion: 'happy',
      },
      { text: 'So nice to see you enjoying "{questtitle}", {player}.', emotion: 'inspired' },
      { text: "No pressure at all, {player} — it'll keep {emoji}", emotion: 'normal' },
    ],
  },
  'quest-complete': {
    subject: [
      { text: '{giver} lights right up: "{line}"', emotion: 'joyous' },
      {
        text: 'You did it, {player}! "{questtitle}" complete — {reward}, well earned {emoji}',
        emotion: 'joyous',
      },
      { text: '{giver} beams. "{line}" And {reward}, just for you {emoji}', emotion: 'happy' },
    ],
    reaction: [
      { text: "Hooray, {player}! We're all so proud — {reward}!", emotion: 'joyous' },
      {
        text: '"{questtitle}" done and dusted — lovely work, {player} {emoji}',
        emotion: 'inspired',
      },
      {
        text: 'Cheers from the whole room, {player}! {reward} suits you {emoji}',
        emotion: 'happy',
      },
    ],
  },
  // ── Round-3 arc beats (notes/r3-design/) — warm by construction ──────────────
  'follower-mood': {
    subject: [
      { text: '{nick} {verb} — anyone can see how loved that one is {emoji}', emotion: 'joyous' },
      {
        text: 'Did you see? {nick} just {verb} next to {player}. Sweetest thing.',
        emotion: 'happy',
      },
      { text: '{nick} looks so content trotting after {player} today {emoji}', emotion: 'happy' },
    ],
    reaction: [
      { text: 'That little one adores you, {player} {emoji}', emotion: 'joyous' },
      { text: 'A well-loved partner is the best sight in town {emoji}', emotion: 'inspired' },
      { text: 'Aww. {nick} is glowing, {player}.', emotion: 'happy' },
    ],
  },
  'champion-crowned': {
    subject: [
      { text: '{player} walked the whole Summit — our CHAMPION! {emoji}', emotion: 'joyous' },
      {
        text: 'The laurel by the square is for {player} — Champion of the region {emoji}',
        emotion: 'joyous',
      },
      {
        text: "Raise a berry juice — {player} cleared the League! We're so proud.",
        emotion: 'inspired',
      },
    ],
    reaction: [
      { text: 'CHAMPION {player}! The whole town is cheering {emoji}', emotion: 'joyous' },
      { text: 'Knew it from your first badge, {player} {emoji}', emotion: 'happy' },
      { text: 'The Hall of Fame will remember this day, {player}.', emotion: 'inspired' },
    ],
  },
  'rival-cheer': {
    subject: [
      { text: '{giver} cups their hands: "Go on, {player}! You\'ve got this!"', emotion: 'joyous' },
      { text: '{giver} grins. "Win or wobble, drinks on me after. Go!"', emotion: 'happy' },
      {
        text: '{giver} is front row again, cheering {player} like always {emoji}',
        emotion: 'happy',
      },
    ],
    reaction: [
      { text: 'Those two push each other the loveliest way {emoji}', emotion: 'inspired' },
      { text: "With {giver} cheering, {player} can't lose {emoji}", emotion: 'happy' },
      { text: 'Friends like that make the circuit worth walking.', emotion: 'normal' },
    ],
  },
  'rival-milestone': {
    subject: [
      {
        text: '{giver} just earned their own badge — "Right behind you, {player}!" {emoji}',
        emotion: 'joyous',
      },
      {
        text: '{giver} taps their badge case, beaming: "Catching up, slowly and happily."',
        emotion: 'happy',
      },
      {
        text: '{giver} says walking the circuit with {player} makes every badge sweeter {emoji}',
        emotion: 'inspired',
      },
    ],
    reaction: [
      { text: 'Side by side up the circuit — love to see it {emoji}', emotion: 'happy' },
      { text: 'Two badge cases filling up together {emoji}', emotion: 'joyous' },
      { text: 'No race, just a shared road. Lovely.', emotion: 'normal' },
    ],
  },
};

/**
 * PEACOCK / SHOW-OFF asides — fond, non-competitive bragging about one's own team.
 * Woven into exchanges so the town visibly "peacocks." Admiration, never one-upmanship.
 */
export const PEACOCK_ASIDES: readonly TemplateEntry[] = [
  {
    text: 'Not to peacock, but my {species} is the prettiest thing on the route {emoji}',
    emotion: 'joyous',
  },
  {
    text: 'Letting {nick} stretch its legs in the square — come say hi if you spot it!',
    emotion: 'happy',
  },
  {
    text: 'I will never stop showing people my {species}. Look at it {emoji}',
    emotion: 'inspired',
  },
  {
    text: "My {species} just learned a flashy new move and I'm insufferable about it now.",
    emotion: 'joyous',
  },
];

/** MUTUAL FLATTERY — warm compliments traded between trainers (and toward the player). */
export const FLATTERY_ASIDES: readonly TemplateEntry[] = [
  {
    text: 'Honestly {subject}, your team always looks so well-loved. It shows {emoji}',
    emotion: 'happy',
  },
  {
    text: '{subject} has the eye for raising happy Pokémon — I take notes, truly.',
    emotion: 'inspired',
  },
  {
    text: '{player}, your {pmon} has such great energy. You clearly dote on it {emoji}',
    emotion: 'joyous',
  },
  {
    text: "Everyone here raises such gorgeous teams. We're spoiled, aren't we {emoji}",
    emotion: 'happy',
  },
];

/**
 * TWO-WAY questions aimed at the PLAYER — the town asks about *your* Pokémon, not just
 * shows theirs. Read passively or answer via the chat box; either is fine.
 */
export const TWO_WAY_PLAYER_QUESTIONS: readonly TemplateEntry[] = [
  {
    text: "{player}, what are you running on your team right now? I'd love to hear about {pmon}.",
    emotion: 'inspired',
  },
  { text: "Ooh {player}, show us a Pokémon! What's your current favorite?", emotion: 'surprised' },
  { text: "{player}, how's {pmon} coming along? Last time it was tiny {emoji}", emotion: 'happy' },
  { text: 'Got any new catches to brag about, {player}? Go on, we love it.', emotion: 'joyous' },
];

/**
 * LORE / KNOW-HOW shared as community culture — how to find rare/legendary species,
 * where things live, gentle breeding/nature tips. Friendly culture, never a lecture.
 */
export const KNOWHOW_ASIDES: readonly TemplateEntry[] = [
  {
    text: 'Tip from me: a calm-nature {species} learns to relax in the day-camp faster. Cozy little thing.',
    emotion: 'inspired',
  },
  {
    text: "If you're after a {rare}, folks say the {biome} at dusk is your best bet — bring patience.",
    emotion: 'normal',
  },
  {
    text: 'For breeding, matching egg groups is the trick — pair them up and let the hatchery do the rest {emoji}',
    emotion: 'happy',
  },
  {
    text: '{species} lives mostly in the {biome}, by the way. Lovely walk to get there.',
    emotion: 'normal',
  },
  {
    text: "A gentle nature won't change stats much, but it sure changes the vibe of your team {emoji}",
    emotion: 'inspired',
  },
  {
    text: 'Heard the {rare} only stirs after a few rumors go round — keep your ear to the chat.',
    emotion: 'surprised',
  },
];

/** FOND-BRAGGING follow-ups that a subject adds about their own mon, building the back-and-forth. */
export const FOND_BRAG_ASIDES: readonly TemplateEntry[] = [
  {
    text: "And it's the sweetest battler too — never gives up, this {species} of mine {emoji}",
    emotion: 'joyous',
  },
  {
    text: 'I could go on about {nick} for hours, honestly. Best decision I ever made.',
    emotion: 'happy',
  },
  {
    text: "Level {level} and still cuddles up after every match. Couldn't be prouder.",
    emotion: 'inspired',
  },
];

/** Friendly memory-flavored asides, mixed in when a speaker has memories of the
 * player — makes "they remember you" land, with callbacks to past sessions. Warm only. */
export const MEMORY_ASIDES: readonly TemplateEntry[] = [
  { text: '(Still smiling about when we {memory}, {player}!)', emotion: 'happy' },
  {
    text: 'By the way {player} — remember when we {memory}? Good times {emoji}',
    emotion: 'joyous',
  },
  { text: 'Always happy to see you, {player}. We {memory}, after all.', emotion: 'happy' },
  {
    text: 'Still telling people about the time we {memory}, {player} {emoji}',
    emotion: 'inspired',
  },
];

/**
 * DIRECT REPLIES to the PLAYER's own typed line / quick-reply (D14 two-way heart).
 *
 * These exist so that when the player SHOUTS into the chat, the town actually
 * ANSWERS them — greeting them by name and reacting to *what they said* ({said}),
 * never wandering off into unrelated ambient banter. Bucketed by the intent of the
 * player's message so the reply lands: a greeting earns a warm welcome-back, a
 * question earns a genuine answer/offer, anything else earns an engaged riff on
 * their words. The first reply addresses the player directly; follow-ups pile on
 * warmly. Strictly warm — the player is a beloved peer, never judged.
 */

/** The player said hello / greeted the room. */
export const PLAYER_GREETING_REPLIES: readonly TemplateEntry[] = [
  { text: 'Oh, {player}! Hi! So glad you stopped by the hearth {emoji}', emotion: 'joyous' },
  { text: 'There you are, {player}! Pull up a stool, it just got cozier.', emotion: 'happy' },
  { text: 'Hey hey, {player}! We were just chatting — come join in {emoji}', emotion: 'happy' },
  {
    text: 'Welcome back, {player}! The room always brightens when you wander in.',
    emotion: 'inspired',
  },
  { text: '{player}! Good to see your face. How was the route today?', emotion: 'surprised' },
];

/** The player asked a question (their line ended in "?" or began with a question word). */
export const PLAYER_QUESTION_REPLIES: readonly TemplateEntry[] = [
  { text: 'Ooh, good question, {player} — "{said}" Let me think...', emotion: 'surprised' },
  { text: '{player} asks: "{said}" — happy to help with that one {emoji}', emotion: 'inspired' },
  {
    text: 'You\'re asking the right room, {player}. About "{said}" — here\'s what I know.',
    emotion: 'happy',
  },
  {
    text: 'Hmm, "{said}"? Stick around, {player}, someone here always knows {emoji}',
    emotion: 'normal',
  },
  {
    text: 'Love that you asked, {player}. "{said}" — let\'s figure it out together.',
    emotion: 'joyous',
  },
];

/** Anything else the player typed — a remark, a brag, a hello-by-way-of-chatter. */
export const PLAYER_GENERAL_REPLIES: readonly TemplateEntry[] = [
  { text: '"{said}" — ha, I love that, {player} {emoji}', emotion: 'happy' },
  { text: 'Right there with you, {player}. "{said}" — same energy here.', emotion: 'joyous' },
  { text: 'Aw, {player} says "{said}" — you always know what to say {emoji}', emotion: 'happy' },
  { text: 'Heh, "{said}". Good to have you in the chat, {player}.', emotion: 'normal' },
  { text: '{player}, "{said}" made me smile. Tell us more!', emotion: 'inspired' },
];

/** Warm second-voice follow-ups so the player's line earns a little back-and-forth. */
export const PLAYER_REPLY_FOLLOWUPS: readonly TemplateEntry[] = [
  { text: 'Seconded! Lovely to have you chiming in, {player} {emoji}', emotion: 'joyous' },
  { text: "What {self} said, {player} — we're glad you spoke up.", emotion: 'happy' },
  { text: 'Right? {player}, you fit right in here {emoji}', emotion: 'happy' },
  { text: "Don't be a stranger, {player}. We love the company.", emotion: 'inspired' },
  {
    text: 'Anytime you want to show off a Pokémon, {player}, we are all eyes {emoji}',
    emotion: 'surprised',
  },
];

/**
 * CAST-REACTION lines — warm, impressed responses when the PLAYER flourishes a move
 * in the tavern (mirrors the show-off pipeline; D14). Each reacts to the *specific*
 * move ({move}) or its element ({movetype}), so the floor reads grounded, not generic:
 * dazzled at the spectacle, admiring the form, never at the player's expense. Strictly
 * warm — admiration and delight only, no comparison, nothing that could sting.
 */
export const CAST_REACTIONS: readonly TemplateEntry[] = [
  { text: 'Whoa — a {move} indoors?! Show-off {emoji}', emotion: 'surprised' },
  { text: 'Did everyone SEE that {move}?! {player}, that was gorgeous!', emotion: 'joyous' },
  { text: 'Nice form, {player}! That {move} had real flair {emoji}', emotion: 'happy' },
  { text: 'Ooh, a {movetype}-type move — I felt that from here, {player}!', emotion: 'surprised' },
  {
    text: 'That {movetype} energy lit up the whole tavern! Encore, {player} {emoji}',
    emotion: 'joyous',
  },
  { text: '{player} just casually pulled off a {move}. Casually! {emoji}', emotion: 'inspired' },
  { text: 'My drink nearly spilled — what a {move}, {player}!', emotion: 'surprised' },
  {
    text: "Now THAT'S how you cast a {move}. Beautiful control, {player}.",
    emotion: 'inspired',
  },
  {
    text: 'The little ones are all wide-eyed at that {movetype} flourish {emoji}',
    emotion: 'happy',
  },
  { text: 'Bravo, {player}! A {move} like that deserves a round of applause.', emotion: 'joyous' },
  {
    text: 'Careful with the {move} near the lanterns — but wow, what a sight {emoji}',
    emotion: 'surprised',
  },
  {
    text: 'I always love a good {movetype} move. You make it look easy, {player}!',
    emotion: 'happy',
  },
  {
    text: 'That {move} sparkled! Teach me your timing sometime, {player} {emoji}',
    emotion: 'inspired',
  },
  { text: 'Warm cheers from our table, {player} — what a lovely {move}!', emotion: 'joyous' },
];
